import { ref, reactive, computed } from 'vue'
import { defineStore } from 'pinia'
import { useParams } from '@/composables/useParams'

import { safeCopy, toast, i18N } from '@/common/util'
import { BannerRowDataType, BackgroundBannerType, LoaderType } from '@/types/types'
import { mediaFileService } from '@/services/mediafile.service'
import { customizationBackgroundBannerService } from '@/services/customizationBackgroundBanner.service'

import systemStore from '@/stores/systemStore'

export const useCustomizationBackgroundBannerStore = defineStore('customizationBackgroundBanner', () => {
  const system = systemStore()

  const { batchMode } = useParams()

  const nStatus = reactive({
    upload: 'initial' as LoaderType,
    save: 'initial' as LoaderType,
    update: 'initial' as LoaderType,
    delete: 'initial' as LoaderType,
    getAll: 'initial' as LoaderType,
    bulkReplication: 'initial' as LoaderType,
  })

  const ambienteMap = {
    'Aprendizado': 'Home',
    'Catalogo': 'Catalog',
    'Ambos': 'Both'
  }

  const loading = computed(() => Object.values(nStatus).some(el => el === 'loading'))
  const uploadProgress = ref(0)
  const totalErrors = ref(0)

  const bannerRows = ref<BannerRowDataType[]>([])
  const bannerRowsTemp = ref<BannerRowDataType[]>([])
  const originalBannerRows = ref<BannerRowDataType[]>([])
  const bannerRowsBatchMode = ref<BannerRowDataType[]>([])

  const uploadBackground = async (
    row: BannerRowDataType,
    index: number,
    updateProgress: () => void
  ) => {
    if (!row.files.background || row.data?.backgroundCopiedFrom) return
  
    try {
      const response = await mediaFileService.save({ file: row.files.background, order: index })
      row.data.planoDeFundoId = response.id
      row.data.planoDeFundoUrl = response.link
  
      delete row.files.background
      updateProgress()
    } catch (error) {
      totalErrors.value++
      toast.open({
        type: 'error',
        title: i18N('feedback.attention'),
        message: i18N('backgroundAndBanner.notifications.uploadBackgroundFail', { index: index + 1, error })
      })
    }
  }
  
  const uploadBanner = async (
    row: BannerRowDataType,
    index: number,
    updateProgress: () => void
  ) => {
    if (!row.files.banner || row.data.tipoRecurso === 'Texto') return
  
    try {
      const response = await mediaFileService.save({ file: row.files.banner, order: index })
      row.data.bannerId = response.id
      row.data.bannerUrl = response.link
  
      delete row.files.banner
      updateProgress()
    } catch (error) {
      totalErrors.value++
      console.error('Erro no upload do banner:', error)
      toast.open({
        type: 'error',
        title: i18N('feedback.attention'),
        message: i18N('backgroundAndBanner.notifications.uploadBannerFail', { index: index + 1, error })
      })
    }
  }
  
  const processUploads = async (): Promise<void> => {
    const rows = batchMode.active ? bannerRowsBatchMode.value : bannerRowsTemp.value
    let completedUploads = 0
  
    // vamos preencher dinamicamente enquanto montamos as promises
    const uploadPromises: Promise<void>[] = []
    let totalUploads = 0
  
    const updateProgress = () => {
      completedUploads++
      uploadProgress.value = Math.round((completedUploads / totalUploads) * 100)
    }
  
    rows.forEach((row, index) => {
      if (row.files.background && !row.data?.backgroundCopiedFrom) {
        totalUploads++
        uploadPromises.push(uploadBackground(row, index, updateProgress))
      }
      if (row.files.banner && row.data.tipoRecurso !== 'Texto') {
        totalUploads++
        uploadPromises.push(uploadBanner(row, index, updateProgress))
      }
    })
  
    if (uploadPromises.length > 0) {
      toast.open({
        type: 'info',
        title: null,
        message: i18N('notifications.uploadingFiles')
      })

      await Promise.all(uploadPromises)

      toast.open({
        type: 'success',
        title: null,
        message: i18N('notifications.uploadCompleted')
      })
    }
  
    uploadProgress.value = 100
  }

  const processCopiedBackgrounds = async (): Promise<void> => {
    const rows = batchMode.active ? bannerRowsBatchMode.value : bannerRowsTemp.value
  
    rows.forEach(row => {
      if (row.data.backgroundCopiedFrom) {
        const copiedItem = rows.find(r => r.data.id === row.data.backgroundCopiedFrom)
  
        if (copiedItem) {
          row.data.tipoPlanoDeFundo = copiedItem.data.tipoPlanoDeFundo
          row.data.planoDeFundoId = copiedItem.data.planoDeFundoId
          row.data.planoDeFundoUrl = copiedItem.data.planoDeFundoUrl
  
          delete row.data.backgroundCopiedFrom
          delete row.files.background
        }
      }
    })
  }

  const processInsertionsAndUpdates = async (): Promise<void> => {
    const insertPromises: Promise<void>[] = []
    const updatePromises: Promise<void>[] = []

    bannerRowsTemp.value.forEach((row, index) => {
      const isNewItem = !row.data.id || row.data.id.startsWith('temp-')
      
      if (isNewItem) {
        delete row.data.id

        const insertPromise = customizationBackgroundBannerService.save(row.data)
          .then((response) => {
            bannerRowsTemp.value[index].data = response.data

            toast.open({
              type: 'success',
              title: i18N('feedback.success'),
              message: i18N('backgroundAndBanner.notifications.insertSuccess', { index: index + 1 })
            })
          })
          .catch((error) => {
            totalErrors.value++
            console.error('Erro na inserção:', error)

            toast.open({
              type: 'error',
              title: i18N('feedback.attention'),
              message: i18N('backgroundAndBanner.notifications.insertFail', { index: index + 1, error })
            })
          })
        
        insertPromises.push(insertPromise)
      } else {
        const originalItem = originalBannerRows.value.find(orig => orig.data.id === row.data.id)
        
        if (originalItem && hasItemChanged(row.data, originalItem.data)) {
          const updatePromise = customizationBackgroundBannerService.update(row.data.id, row.data)
            .then((response) => {
              bannerRowsTemp.value[index].data = response.data

              toast.open({
                type: 'success',
                title: i18N('feedback.success'),
                message: i18N('backgroundAndBanner.notifications.updateSuccess', { index: index + 1 })
              })
            })
            .catch((error) => {
              totalErrors.value++
              console.error('Erro na atualização:', error)

              toast.open({
                type: 'error',
                title: i18N('feedback.attention'),
                message: i18N('backgroundAndBanner.notifications.updateFail', { index: index + 1, error })
              })
            })
          
          updatePromises.push(updatePromise)
        }
      }
    })

    // Executa inserções e atualizações em paralelo
    await Promise.all([
      ...insertPromises,
      ...updatePromises
    ])
  }

  const processDeletions = async (): Promise<void> => {
    // Filtra apenas os itens que sumiram da lista atual
    const itemsToDelete = originalBannerRows.value.filter(originalRow => {
      const stillExists = bannerRowsTemp.value.some(
        currentRow => currentRow.data.id === originalRow.data.id
      )

      return (
        !stillExists &&
        originalRow.data.id &&
        !originalRow.data.id.startsWith("temp-")
      )
    })

    // Mapeia cada item em uma promessa de exclusão
    const deletePromises = itemsToDelete.map(row =>
      customizationBackgroundBannerService.delete({ id: row.data.id })
        .catch(error => {
          totalErrors.value++
          console.error("Erro na exclusão:", error)

          toast.open({
            type: "error",
            title: i18N("feedback.attention"),
            message: i18N("backgroundAndBanner.notifications.deleteFail", {
              id: row.data.id,
              error
            })
          })
        })
    )

    // Executa todas as exclusões em paralelo
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises)
    }
  }

  const saveBanners = async (rows: BannerRowDataType[]): Promise<void> => {
    nStatus.save = 'loading'

    try {
      uploadProgress.value = 0
      totalErrors.value = 0
      bannerRowsTemp.value = [...rows]

      // ETAPA 1: Uploads
      await processUploads()

      // ETAPA 1.5: Processa planos de fundo copiados
      await processCopiedBackgrounds()
      
      // ETAPA 2: Inserções e Atualizações
      await processInsertionsAndUpdates()
      
      // ETAPA 3: Exclusões
      await processDeletions()

      if (totalErrors.value > 0) return

      await loadExistingData()

      toast.open({
        type: 'success',
        title: i18N('feedback.success'),
        message: i18N('backgroundAndBanner.notifications.saveSuccess')
      })
      
      nStatus.save = 'success'
    } catch (error) {
      nStatus.save = 'error'
      console.error('Erro no salvamento dos banners:', error)

      toast.open({
        type: 'error',
        title: i18N('feedback.attention'),
        message: i18N('backgroundAndBanner.notifications.saveFail', { error })
      })

    } finally {
      nStatus.save = 'complete'
      uploadProgress.value = 0
      totalErrors.value = 0
    }
  }

  const resetExistingData = () => {
    bannerRowsTemp.value = []
    originalBannerRows.value = []
    bannerRows.value = []

    system.setBgsAndBanners([])
    nStatus.getAll = 'initial'
  }

  const loadExistingData = async (): Promise<BannerRowDataType[]> => {
    nStatus.getAll = 'loading'

    try {
      const { data: existingData } = await customizationBackgroundBannerService.getAll()

      if (existingData.length === 0) {
        bannerRowsTemp.value = []
        originalBannerRows.value = []
        bannerRows.value = []

        system.clientSettings.settingsBackgroundBanner = []

        nStatus.getAll = 'success'
        return
      }
      
      const loadedRows: BannerRowDataType[] = existingData?.map(item => ({
        data: item,
        files: {}
      }))
      
      
      if (!batchMode.active) {
        bannerRowsTemp.value = safeCopy(loadedRows)
        originalBannerRows.value = safeCopy(loadedRows)
        bannerRows.value = safeCopy(loadedRows)

        system.setBgsAndBanners(bannerRows.value?.map(row => ({
          id: row.data.id,
          active: row.data.active,
          order: row.data.ordem,
          target: (ambienteMap[row.data.ambiente])?.toLowerCase(),
          backgroundType: row.data.tipoPlanoDeFundo?.toLocaleLowerCase(),
          backgroundId: row.data.planoDeFundoId || null,
          backgroundSrc: row.data.planoDeFundoUrl || '',
          resourceType: row.data.tipoRecurso?.toLocaleLowerCase(),
          bannerId: row.data.bannerId || null,
          bannerImg: row.data.bannerUrl || '',
          redirectLink: row.data.linkRedirecionamentoBanner || '',
          openInNewTab: row.data.newTab || false,
          isPublic: row.data.public || false,
          duration: row.data.duracao,
          content: row.data.markdownConteudo || '',
          backgroundColor: row.data.backgroundConteudo || ''
        })) || [])
      }

      nStatus.getAll = 'success'
      return loadedRows
      
    } catch (error) {
      bannerRowsTemp.value = []
      originalBannerRows.value = []
      bannerRows.value = []
      nStatus.getAll = 'error'

      toast.open({
        type: 'error',
        title: i18N('feedback.attention'),
        message: i18N('backgroundAndBanner.notifications.loadError', { error })
      })
    }
  }

  const loadIfNot = () => {
    if (nStatus.getAll === 'initial' || nStatus.getAll === 'error') {
      return loadExistingData()
    }
  }

  const hasItemChanged = (current: BackgroundBannerType, original: BackgroundBannerType): boolean => {
    return Object.keys(current).some(
      key => current[key as keyof BackgroundBannerType] !== original[key as keyof BackgroundBannerType]
    )
  }

  const resetChanges = () => {
    bannerRows.value = [...originalBannerRows.value]
  }

  const bulkReplication = async (contractIds: string[]): Promise<void> => {
    nStatus.bulkReplication = 'loading'

    try {
      uploadProgress.value = 0
      totalErrors.value = 0

      // ETAPA 1: Uploads
      await processUploads()

      // ETAPA 1.5: Processa planos de fundo copiados
      await processCopiedBackgrounds()

      toast.open({
        type: 'info',
        title: null,
        message: i18N('notifications.bulkReplicationInit')
      })

      const rows = bannerRowsBatchMode.value.map(({ data }) => {
        delete data.id
        return data
      })
      
      await customizationBackgroundBannerService.bulkReplication(contractIds, rows)
   
    } catch (error) {
      nStatus.bulkReplication = 'error'
      console.error('Erro na replicação em massa:', error)

      toast.error({
        title: i18N('feedback.attention'),
        message: i18N('notifications.bulkReplicationFail', { error })
      })
    }

    nStatus.bulkReplication = 'success'
  }

  return {
    loading,
    uploadProgress,
    bannerRows,
    bannerRowsTemp,
    originalBannerRows,
    bannerRowsBatchMode,
    
    // Funções principais
    resetChanges,
    saveBanners,
    loadExistingData,
    loadIfNot,
    bulkReplication,
    
    // Funções auxiliares
    hasItemChanged,
    resetExistingData
  }
})

export default useCustomizationBackgroundBannerStore